import { supabase } from '@/integrations/supabase/client';
import { Mission, PlayerMission, getPlayerLevel, calculatePlayerBadges, WEEKLY_PLAYTIME_MISSION, calculateCreatorBadges } from '@/data/missions';
import { getXpForLevel, getLevelFromXp, getLevelRewards } from '@/data/leveling';

export interface PlayerStats {
  coins: number;
  trust_score: number;
  level: string; // legacy rank
  xp: number;
  player_level: number;
  badges: string[];
  total_playtime: number;
  games_tested: number;
  reviews_count: number;
  social_actions: number;
  days_since_signup: number;
  avg_playtime_ratio: number;
  likes_count: number;
  boost_end_date?: string;
}

export interface GameCoins {
  id: string;
  game_id: string;
  coins_cost: number;
  game?: {
    title: string;
    cover_image_url?: string;
  };
}

// Récupérer les missions disponibles
export async function getAvailableMissions(): Promise<Mission[]> {
  const { data, error } = await supabase
    .from('missions')
    .select('*')
    .eq('is_active', true)
    .order('type', { ascending: true });

  if (error) {
    console.error('Error fetching missions:', error);
    return [];
  }

  return data || [];
}

// Récupérer les missions complétées par un joueur
export async function getPlayerMissions(playerId: string): Promise<PlayerMission[]> {
  const { data, error } = await supabase
    .from('player_missions')
    .select(`
      *,
      mission:missions(*)
    `)
    .eq('player_id', playerId)
    .order('completed_at', { ascending: false });

  if (error) {
    console.error('Error fetching player missions:', error);
    return [];
  }

  return data || [];
}

// Récupérer toutes les missions complétées (pour vérifier one-time missions)
export async function getAllMissions(playerId: string): Promise<PlayerMission[]> {
  const { data, error } = await supabase
    .from('player_missions')
    .select(`
      *,
      mission:missions(*)
    `)
    .eq('player_id', playerId);

  if (error) {
    console.error('Error fetching all missions:', error);
    return [];
  }

  return data || [];
}

// Récupérer les missions complétées aujourd'hui
export async function getTodayMissions(playerId: string): Promise<PlayerMission[]> {
  const today = new Date().toISOString().split('T')[0];

  const { data, error } = await supabase
    .from('player_missions')
    .select(`
      *,
      mission:missions(*)
    `)
    .eq('player_id', playerId)
    .eq('completion_date', today);

  if (error) {
    console.error('Error fetching today missions:', error);
    return [];
  }

  return data || [];
}

// Récupérer les missions complétées cette semaine
export async function getWeekMissions(playerId: string): Promise<PlayerMission[]> {
  const startOfWeek = new Date();
  startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());
  startOfWeek.setHours(0, 0, 0, 0);

  const { data, error } = await supabase
    .from('player_missions')
    .select(`
      *,
      mission:missions(*)
    `)
    .eq('player_id', playerId)
    .gte('completed_at', startOfWeek.toISOString());

  if (error) {
    console.error('Error fetching week missions:', error);
    return [];
  }

  return data || [];
}

// Complete a mission
export async function completeMission(playerId: string, missionId: string, pointsOverride?: number): Promise<boolean> {
  try {
    // Check if user is a player (only players can complete missions)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('id', playerId)
      .single();

    if (profileError || !profile || profile.user_type !== 'player') {
      return false; // Only players can complete missions
    }

    // Check if mission exists and is active
    const { data: mission, error: missionError } = await supabase
      .from('missions')
      .select('*, xp_reward')
      .eq('id', missionId)
      .eq('is_active', true)
      .single();

    if (missionError || !mission) {
      console.error('Mission not found or inactive:', missionError);
      return false;
    }

    // Check if mission hasn't been completed today (for daily missions)
    if (mission.type === 'daily') {
      const today = new Date().toISOString().split('T')[0];
      const { data: allMissions } = await supabase
        .from('player_missions')
        .select('id, completed_at, completion_date')
        .eq('player_id', playerId)
        .eq('mission_id', missionId);

      // Check if any mission was completed today using the completion_date column
      const existingMission = allMissions?.find(m => {
        const missionDate = m.completion_date || new Date(m.completed_at).toISOString().split('T')[0];
        return missionDate === today;
      });

      if (existingMission) {
        return false;
      }
    }

    // Check if mission hasn't been completed (for one-time missions)
    if (mission.type === 'one_time') {
      const { data: existingMission } = await supabase
        .from('player_missions')
        .select('id')
        .eq('player_id', playerId)
        .eq('mission_id', missionId)
        .single();

      if (existingMission) {
        return false;
      }
    }

    // Check for active boost
    const { data: playerProfile } = await supabase
      .from('profiles')
      .select('boost_end_date')
      .eq('id', playerId)
      .single();

    const isBoostActive = playerProfile?.boost_end_date && new Date(playerProfile.boost_end_date) > new Date();

    let pointsToEarn = pointsOverride !== undefined ? pointsOverride : mission.points_reward;

    // Double points if boost is active (only for daily and weekly missions)
    if (isBoostActive && (mission.type === 'daily' || mission.type === 'weekly')) {
      pointsToEarn *= 2;
    }

    // Add completed mission
    const { error: missionError2 } = await supabase
      .from('player_missions')
      .insert({
        player_id: playerId,
        mission_id: missionId,
        points_earned: pointsToEarn,
        completion_date: new Date().toISOString().split('T')[0]
      });

    if (missionError2) {
      console.error('Error completing mission:', missionError2);
      return false;
    }

    // Add coins to player
    if (pointsToEarn > 0) {
      await addCoins(playerId, pointsToEarn);
    }

    // Add XP to player
    const xpToEarn = (mission as any).xp_reward || 0;
    if (xpToEarn > 0) {
      await addXp(playerId, xpToEarn);
    }

    // Recalculer les badges si cette action peut affecter les badges
    // Les missions qui affectent les badges : like_game, like_developer, add_favorite, visit_game_page
    const badgeAffectingMissions = ['like_game', 'like_developer', 'add_favorite', 'visit_game_page'];
    if (badgeAffectingMissions.includes(mission.name)) {
      // Recalculer en arrière-plan (ne pas bloquer)
      recalculatePlayerBadges(playerId).catch(error => {
        console.error('Error recalculating badges after mission:', error);
      });
    }

    return true;
  } catch (error) {
    console.error('Error completing mission:', error);
    return false;
  }
}

// Complete mission by action type
export async function completeMissionByAction(playerId: string, action: string, targetId?: string): Promise<boolean> {
  try {
    // Check if user is a player (only players can complete missions)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('id', playerId)
      .single();

    if (profileError || !profile || profile.user_type !== 'player') {
      return false; // Only players can complete missions
    }

    // Get mission by action
    const { data: mission, error: missionError } = await supabase
      .from('missions')
      .select('*')
      .eq('name', action)
      .eq('is_active', true)
      .single();

    if (missionError || !mission) {
      return false;
    }

    // Check if mission can be completed
    if (mission.type === 'daily') {
      const today = new Date().toISOString().split('T')[0];
      const { data: allMissions } = await supabase
        .from('player_missions')
        .select('id, completed_at, completion_date')
        .eq('player_id', playerId)
        .eq('mission_id', mission.id);

      // Check if any mission was completed today using the completion_date column
      const existingMission = allMissions?.find(m => {
        const missionDate = m.completion_date || new Date(m.completed_at).toISOString().split('T')[0];
        return missionDate === today;
      });

      if (existingMission) {
        return false; // Already completed today
      }
    }

    if (mission.type === 'one_time') {
      const { data: existingMissions } = await supabase
        .from('player_missions')
        .select('id')
        .eq('player_id', playerId)
        .eq('mission_id', mission.id);

      if (existingMissions && existingMissions.length > 0) {
        return false; // Already completed
      }
    }

    // Complete the mission
    return await completeMission(playerId, mission.id);
  } catch (error) {
    console.error('Error completing mission by action:', error);
    return false;
  }
}

// Check and complete the "Play purchased games" weekly mission
export async function checkPlayPurchasedGamesMission(playerId: string): Promise<boolean> {
  try {
    // Check if user is a player (only players can complete missions)
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('user_type')
      .eq('id', playerId)
      .single();

    if (profileError || !profile || profile.user_type !== 'player') {
      return false; // Only players can complete missions
    }

    // Get the weekly playtime mission
    const { data: mission, error: missionError } = await supabase
      .from('missions')
      .select('*')
      .eq('name', 'weekly_playtime')
      .eq('is_active', true)
      .single();

    if (missionError || !mission) {
      return false;
    }

    // Check if mission was completed within the cooldown period
    const cooldownDate = new Date();
    cooldownDate.setDate(cooldownDate.getDate() - WEEKLY_PLAYTIME_MISSION.COOLDOWN_DAYS);

    const { data: recentCompletions } = await supabase
      .from('player_missions')
      .select('completed_at')
      .eq('player_id', playerId)
      .eq('mission_id', mission.id)
      .gte('completed_at', cooldownDate.toISOString());

    // If already completed within the cooldown period, don't complete again
    if (recentCompletions && recentCompletions.length > 0) {
      return false;
    }

    // Get purchased games from the purchase window
    const purchaseWindowDate = new Date();
    purchaseWindowDate.setDate(purchaseWindowDate.getDate() - WEEKLY_PLAYTIME_MISSION.PURCHASE_WINDOW_DAYS);

    const { data: recentPurchases, error: purchasesError } = await supabase
      .from('purchased_keys')
      .select('id, game_id, purchased_at')
      .eq('player_id', playerId)
      .gte('purchased_at', purchaseWindowDate.toISOString());

    if (purchasesError || !recentPurchases || recentPurchases.length === 0) {
      return false;
    }

    // Get playtime stats for these games from steam_game_stats (has playtime_2weeks)
    const { data: steamStats } = await supabase
      .from('steam_game_stats')
      .select('game_id, playtime_2weeks')
      .eq('player_id', playerId);

    if (!steamStats) {
      return false;
    }

    // Check if at least one recently purchased game has more than the required playtime in the last 2 weeks
    const hasValidPlaytime = recentPurchases.some(purchase => {
      const stats = steamStats.find(s => s.game_id === purchase.game_id);
      if (!stats) return false;

      // Check playtime and ensure purchase is within the purchase window
      const purchaseDate = new Date(purchase.purchased_at);
      const daysSincePurchase = Math.floor((Date.now() - purchaseDate.getTime()) / (1000 * 60 * 60 * 24));

      // Game must be purchased within the window AND have enough playtime in the last 2 weeks
      return daysSincePurchase < WEEKLY_PLAYTIME_MISSION.PURCHASE_WINDOW_DAYS &&
        (stats.playtime_2weeks || 0) >= WEEKLY_PLAYTIME_MISSION.REQUIRED_PLAYTIME_MINUTES;
    });

    if (hasValidPlaytime) {
      // Complete the mission
      return await completeMission(playerId, mission.id);
    }

    return false;
  } catch (error) {
    console.error('Error checking play purchased games mission:', error);
    return false;
  }
}

// Add coins to a player
export async function addCoins(playerId: string, coins: number): Promise<boolean> {
  try {
    // Get current coins
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('coins')
      .eq('id', playerId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching profile:', profileError);
      return false;
    }

    const newCoins = profile.coins + coins;

    // Update coins
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ coins: newCoins })
      .eq('id', playerId);

    if (updateError) {
      console.error('Error updating coins:', updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error adding coins:', error);
    return false;
  }
}

// Add XP to a player and check for level-up
export async function addXp(playerId: string, xpToAdd: number): Promise<boolean> {
  try {
    // Get current XP and level
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('xp, player_level')
      .eq('id', playerId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching profile for XP update:', profileError);
      return false;
    }

    const newXp = (profile.xp || 0) + xpToAdd;
    const oldLevel = profile.player_level || 1;
    const newLevel = getLevelFromXp(newXp);

    // Update XP and level
    interface ProfileUpdate {
      xp: number;
      player_level?: number;
    }
    const updateData: ProfileUpdate = { xp: newXp };

    if (newLevel > oldLevel) {
      updateData.player_level = newLevel;
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update(updateData)
      .eq('id', playerId);

    if (updateError) {
      console.error('Error updating XP:', updateError);
      return false;
    }

    // Process rewards for each level reached
    if (newLevel > oldLevel) {
      for (let l = oldLevel + 1; l <= newLevel; l++) {
        await awardLevelRewards(playerId, l);
      }
    }

    return true;
  } catch (error) {
    console.error('Error adding XP:', error);
    return false;
  }
}

// Award rewards for leveling up
export async function awardLevelRewards(playerId: string, level: number): Promise<void> {
  const rewards = getLevelRewards(level);
  if (!rewards) return;

  try {
    // 1. Award Gems (coins in profiles table)
    if (rewards.gems) {
      await addCoins(playerId, rewards.gems);
    }

    // 2. Award Boosts (inventory AND profiles table)
    if (rewards.boost_days) {
      // Find a shop item for this boost duration
      const { data: boostItems } = await supabase
        .from('shop_items')
        .select('id, metadata')
        .eq('type', 'boost')
        .eq('is_active', true);

      const shopItem = boostItems?.find(i => i.metadata?.duration_days == rewards.boost_days);

      if (shopItem) {
        // Try to add to inventory (upsert to handle if they already have it)
        try {
          // Check if item exists first to decide on insert vs update (or just upsert)
          const { error: invError } = await supabase
            .from('user_inventory')
            .upsert({
              user_id: playerId,
              item_id: shopItem.id,
              item_type: 'boost',
              is_active: true, // Mark as active
              acquired_at: new Date().toISOString()
            }, { onConflict: 'user_id,item_id' });

          if (invError) console.error('Error upserting boost inventory:', invError);
        } catch (e) {
          console.error('Exception upserting boost inventory:', e);
        }
      }

      // Update boost_end_date in profiles for immediate effect
      const { data: profile } = await supabase
        .from('profiles')
        .select('boost_end_date')
        .eq('id', playerId)
        .single();

      const currentBoostEnd = profile?.boost_end_date ? new Date(profile.boost_end_date) : new Date();
      // Ensure we don't start from the past if the boost expired
      const baseDate = currentBoostEnd.getTime() > Date.now() ? currentBoostEnd.getTime() : Date.now();
      const newBoostEnd = new Date(baseDate + rewards.boost_days * 24 * 60 * 60 * 1000);

      await supabase
        .from('profiles')
        .update({ boost_end_date: newBoostEnd.toISOString() })
        .eq('id', playerId);
    }

    // 3. Award Borders (inventory and profiles table)
    if (rewards.border_style) {
      // Find the shop item for this border
      const { data: shopItem } = await supabase
        .from('shop_items')
        .select('id')
        .eq('type', 'border')
        .filter('metadata->>style_slug', 'eq', rewards.border_style)
        .maybeSingle();

      if (shopItem) {
        // Add to inventory if not already there
        await supabase
          .from('user_inventory')
          .insert({
            user_id: playerId,
            item_id: shopItem.id,
            item_type: 'border',
            is_active: false
          });

        // We don't auto-activate borders because users might want to keep their current one
      }
    }
  } catch (error) {
    console.error(`Error awarding rewards for level ${level}:`, error);
  }
}

// Spend coins
export async function spendCoins(playerId: string, coins: number): Promise<boolean> {
  try {
    // Get current coins
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('coins')
      .eq('id', playerId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching profile:', profileError);
      return false;
    }

    if (profile.coins < coins) {
      console.error('Insufficient coins');
      return false;
    }

    const newCoins = profile.coins - coins;

    // Update coins
    const { error: updateError } = await supabase
      .from('profiles')
      .update({ coins: newCoins })
      .eq('id', playerId);

    if (updateError) {
      console.error('Error updating coins:', updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error spending coins:', error);
    return false;
  }
}

// Recalculer et mettre à jour les badges d'un joueur (appelé quand une action pertinente se produit)
export async function recalculatePlayerBadges(playerId: string, gamesCompleted100: number = 0): Promise<string[] | null> {
  try {
    // Récupérer les statistiques nécessaires
    const { data: profile } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('id', playerId)
      .single();

    if (!profile) return null;

    const { data: playtimeData } = await supabase
      .from('playtime_tracking')
      .select('steam_playtime, required_playtime, playtime_ratio')
      .eq('player_id', playerId);

    const { data: socialData } = await supabase
      .from('player_missions')
      .select(`
        *,
        missions(name)
      `)
      .eq('player_id', playerId)
      .in('missions.name', [
        'like_game',
        'like_developer',
        'add_favorite',
        'visit_game_page'
      ]);

    const { data: likesData } = await supabase
      .from('game_likes')
      .select('id', { count: 'exact' })
      .eq('player_id', playerId);

    // Calculer les statistiques
    const totalPlaytime = playtimeData?.reduce((sum, item) => sum + (item.steam_playtime || 0), 0) || 0;
    const avgPlaytimeRatio = playtimeData?.length > 0
      ? playtimeData.reduce((sum, item) => sum + (item.playtime_ratio || 0), 0) / playtimeData.length
      : 0;
    const gamesTested = playtimeData?.length || 0;
    const socialActions = socialData?.length || 0;
    const likesCount = likesData?.length || 0;

    const daysSinceSignup = Math.floor(
      (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Calculer les nouveaux badges
    const badges = calculatePlayerBadges({
      games_tested: gamesTested,
      total_playtime: totalPlaytime,
      days_since_signup: daysSinceSignup,
      avg_playtime_ratio: avgPlaytimeRatio,
      social_actions: socialActions,
      likes_count: likesCount,
      games_completed_100: gamesCompleted100
    });

    // Mettre à jour les badges dans la BDD
    await supabase
      .from('profiles')
      .update({ badges: badges })
      .eq('id', playerId);

    return badges;
  } catch (error) {
    console.error('Error recalculating badges:', error);
    return null;
  }
}

// Récupérer les statistiques d'un joueur (utilise les badges stockés dans la BDD)
export async function getPlayerStats(playerId: string, gamesCompleted100: number = 0, forceRecalculate: boolean = false): Promise<PlayerStats | null> {
  try {
    // Get base profile avec badges stockés
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('coins, trust_score, level, badges, created_at, boost_end_date, xp, player_level')
      .eq('id', playerId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching profile:', profileError);
      return null;
    }

    // Récupérer les statistiques de jeu
    const { data: playtimeData, error: playtimeError } = await supabase
      .from('playtime_tracking')
      .select('steam_playtime, required_playtime, playtime_ratio')
      .eq('player_id', playerId);

    if (playtimeError) {
      console.error('Error fetching playtime data:', playtimeError);
    }

    // Get social actions (likes, favorites, etc.)
    const { data: socialData, error: socialError } = await supabase
      .from('player_missions')
      .select(`
        *,
        missions(name)
      `)
      .eq('player_id', playerId)
      .in('missions.name', [
        'like_game',
        'like_developer',
        'add_favorite',
        'visit_game_page'
      ]);

    if (socialError) {
      console.error('Error fetching social data:', socialError);
    }

    // Calculer les statistiques
    const totalPlaytime = playtimeData?.reduce((sum, item) => sum + (item.steam_playtime || 0), 0) || 0;
    const avgPlaytimeRatio = playtimeData?.length > 0
      ? playtimeData.reduce((sum, item) => sum + (item.playtime_ratio || 0), 0) / playtimeData.length
      : 0;

    // Le nombre de jeux testés correspond au nombre d'entrées dans playtime_tracking
    const gamesTested = playtimeData?.length || 0;
    const socialActions = socialData?.length || 0;

    const daysSinceSignup = Math.floor(
      (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Récupérer le nombre de likes
    const { data: likesData } = await supabase
      .from('game_likes')
      .select('id', { count: 'exact' })
      .eq('player_id', playerId);

    const likesCount = likesData?.length || 0;

    // Utiliser les badges stockés dans la BDD (ou recalculer si demandé)
    let badges = profile.badges || [];
    if (forceRecalculate) {
      const recalculatedBadges = await recalculatePlayerBadges(playerId, gamesCompleted100);
      if (recalculatedBadges) {
        badges = recalculatedBadges;
      }
    }

    // Mettre à jour le niveau si nécessaire
    const newLevel = getPlayerLevel(profile.trust_score);
    if (profile.level !== newLevel) {
      await supabase
        .from('profiles')
        .update({ level: newLevel })
        .eq('id', playerId);
    }

    return {
      coins: profile.coins,
      trust_score: profile.trust_score,
      level: newLevel,
      xp: profile.xp || 0,
      player_level: profile.player_level || 1,
      badges: badges,
      total_playtime: totalPlaytime,
      games_tested: gamesTested,
      reviews_count: 0, // TODO: implement review system
      social_actions: socialActions,
      days_since_signup: daysSinceSignup,
      avg_playtime_ratio: avgPlaytimeRatio,
      likes_count: likesCount,
      boost_end_date: profile.boost_end_date
    };
  } catch (error) {
    console.error('Error getting player stats:', error);
    return null;
  }
}

export interface CreatorStats {
  keys_received: number;
  games_covered: number;
  content_created: number;
  total_requests: number;
  approved_requests: number;
  pending_requests: number;
  rejected_requests: number;
  total_playtime: number; // en minutes
  badges: string[];
  days_since_signup: number;
}

// Recalculer et mettre à jour les badges d'un créateur (appelé quand une action pertinente se produit)
export async function recalculateCreatorBadges(creatorId: string): Promise<string[] | null> {
  try {
    // Récupérer les statistiques nécessaires
    const { data: profile } = await supabase
      .from('profiles')
      .select('created_at')
      .eq('id', creatorId)
      .single();

    if (!profile) return null;

    // Récupérer les key requests
    const { data: requests } = await supabase
      .from('key_requests')
      .select('status, is_covered, game_id')
      .eq('creator_id', creatorId);

    const approvedRequests = requests?.filter(r => r.status === 'approved') || [];
    const keysReceived = approvedRequests.length;
    const gamesCovered = approvedRequests.filter(r => r.is_covered).length;
    const totalRequests = requests?.length || 0;

    // Calculer le nombre réel de coverages depuis la table coverage
    const { count: coverageCount } = await supabase
      .from('coverage')
      .select('*', { count: 'exact', head: true })
      .eq('creator_id', creatorId);

    const contentCreated = coverageCount || 0;

    // Récupérer le playtime depuis steam_game_stats pour les jeux approuvés
    const gameIds = approvedRequests.map(r => r.game_id);
    let totalPlaytime = 0;
    if (gameIds.length > 0) {
      const { data: playtimeData } = await supabase
        .from('steam_game_stats')
        .select('playtime_forever')
        .eq('player_id', creatorId)
        .in('game_id', gameIds);

      totalPlaytime = playtimeData?.reduce((sum, item) => sum + (item.playtime_forever || 0), 0) || 0;
    }

    const daysSinceSignup = Math.floor(
      (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Calculer les nouveaux badges
    const badges = calculateCreatorBadges({
      keys_received: keysReceived,
      content_created: contentCreated,
      games_covered: gamesCovered,
      days_since_signup: daysSinceSignup,
      total_playtime: totalPlaytime,
      requests_made: totalRequests
    });

    // Mettre à jour les badges et cover_content dans la BDD
    await supabase
      .from('profiles')
      .update({
        badges: badges,
        cover_content: contentCreated
      })
      .eq('id', creatorId);

    return badges;
  } catch (error) {
    console.error('Error recalculating creator badges:', error);
    return null;
  }
}

// Récupérer les statistiques d'un créateur
export async function getCreatorStats(creatorId: string, forceRecalculate: boolean = false): Promise<CreatorStats | null> {
  try {
    // Get base profile avec badges stockés
    const { data: profile, error: profileError } = await supabase
      .from('profiles')
      .select('badges, created_at, cover_content')
      .eq('id', creatorId)
      .single();

    if (profileError || !profile) {
      console.error('Error fetching profile:', profileError);
      return null;
    }

    // Récupérer les key requests
    const { data: requests, error: requestsError } = await supabase
      .from('key_requests')
      .select('status, is_covered, game_id')
      .eq('creator_id', creatorId);

    if (requestsError) {
      console.error('Error fetching key requests:', requestsError);
    }

    const requestsData = requests || [];
    const totalRequests = requestsData.length;
    const approvedRequests = requestsData.filter(r => r.status === 'approved');
    const pendingRequests = requestsData.filter(r => r.status === 'pending');
    const rejectedRequests = requestsData.filter(r => r.status === 'rejected');
    const keysReceived = approvedRequests.length;
    const gamesCovered = approvedRequests.filter(r => r.is_covered).length;

    // Calculer le nombre de coverages depuis la table coverage
    const { count: coverageCount } = await supabase
      .from('coverage')
      .select('*', { count: 'exact', head: true })
      .eq('creator_id', creatorId);

    const contentCreated = coverageCount || (profile as any)?.cover_content || 0;

    // Si le compteur en BDD ne correspond pas, le mettre à jour
    let shouldRecalculateBadges = false;
    if ((profile as any)?.cover_content !== contentCreated) {
      await supabase
        .from('profiles')
        .update({ cover_content: contentCreated })
        .eq('id', creatorId);

      // Marquer qu'il faut recalculer les badges car le compteur a changé
      shouldRecalculateBadges = true;
    }

    // Récupérer le playtime depuis steam_game_stats pour les jeux approuvés
    const approvedGameIds = approvedRequests.map(r => r.game_id);
    let totalPlaytime = 0;
    if (approvedGameIds.length > 0) {
      const { data: playtimeData } = await supabase
        .from('steam_game_stats')
        .select('playtime_forever')
        .eq('player_id', creatorId)
        .in('game_id', approvedGameIds);

      totalPlaytime = playtimeData?.reduce((sum, item) => sum + (item.playtime_forever || 0), 0) || 0;
    }

    const daysSinceSignup = Math.floor(
      (Date.now() - new Date(profile.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );

    // Utiliser les badges stockés dans la BDD (ou recalculer si demandé ou si le compteur a changé)
    let badges = profile.badges || [];
    if (forceRecalculate || badges.length === 0 || shouldRecalculateBadges) {
      const recalculatedBadges = await recalculateCreatorBadges(creatorId);
      if (recalculatedBadges) {
        badges = recalculatedBadges;
      }
    }

    return {
      keys_received: keysReceived,
      games_covered: gamesCovered,
      content_created: contentCreated,
      total_requests: totalRequests,
      approved_requests: approvedRequests.length,
      pending_requests: pendingRequests.length,
      rejected_requests: rejectedRequests.length,
      total_playtime: totalPlaytime,
      badges: badges,
      days_since_signup: daysSinceSignup
    };
  } catch (error) {
    console.error('Error getting creator stats:', error);
    return null;
  }
}

// Calculate coins cost based on game price
export function calculateGameCoins(price: number): number {
  if (price <= 0) return 100; // Free games cost 100 coins
  if (price <= 5) return 125; // 0-5€ = 100 coins
  if (price <= 10) return 150;
  if (price <= 15) return 200;
  if (price <= 20) return 250;
  if (price <= 25) return 300;
  return 350;
}

// Get coins cost for a game
export async function getGameCoins(gameId: string): Promise<number> {
  try {
    const { data: game, error } = await supabase
      .from('games')
      .select('price')
      .eq('id', gameId)
      .single();

    if (error || !game) {
      console.error('Error fetching game price:', error);
      return 100; // Default cost
    }

    return calculateGameCoins(game.price || 0);
  } catch (error) {
    console.error('Error getting game coins:', error);
    return 100; // Default cost
  }
}

// Définir le coût en points d'un jeu
export async function setGamePoints(gameId: string, pointsCost: number): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_points')
      .upsert({
        game_id: gameId,
        points_cost: pointsCost
      });

    if (error) {
      console.error('Error setting game points:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error setting game points:', error);
    return false;
  }
}

// Calculer le coût en points basé sur la durée et le prix
export function calculateGamePoints(avgPlaytimeHours: number, price: number): number {
  // Formule: (durée en heures * 10) + (prix * 0.1)
  // Exemple: jeu de 10h à 20€ = (10 * 10) + (20 * 0.1) = 102 points
  return Math.round((avgPlaytimeHours * 10) + (price * 0.1));
}

// Get developer statistics
export async function getDeveloperStats(developerId: string): Promise<any> {
  try {
    // Fetch all games for this developer along with their key requests
    const { data: games, error } = await supabase
      .from('games')
      .select('id, is_published, key_requests(status)')
      .eq('developer_id', developerId);

    if (error) {
      console.error('Error fetching developer stats:', error);
      return null;
    }

    const gamesData = games || [];
    const publishedGames = gamesData.filter(g => g.is_published).length;

    let totalRequests = 0;
    let approvedCount = 0;
    let rejectedCount = 0;
    let pendingCount = 0;

    gamesData.forEach(game => {
      const requests = (game as any).key_requests || [];
      totalRequests += requests.length;
      requests.forEach((r: any) => {
        if (r.status === 'approved') approvedCount++;
        else if (r.status === 'rejected') rejectedCount++;
        else if (r.status === 'pending') pendingCount++;
      });
    });

    return {
      total_games: publishedGames, // Match screenshot "4 Published games"
      total_requests: totalRequests,
      approved_requests: approvedCount,
      rejected_requests: rejectedCount,
      pending_requests: pendingCount
    };
  } catch (error) {
    console.error('Error getting developer stats:', error);
    return null;
  }
}
